import React, { useCallback, useEffect, useState } from 'react';
import { getSortState, Translate, translate } from 'react-jhipster';
import { RouteComponentProps } from 'react-router-dom';

import { Box } from '@mui/material';
import { AUTHORITIES } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { NoData } from 'app/shared/components/no-data/NoData';
import { PageFilters } from 'app/shared/components/page-filters/PageFilters';
import { PageHeader } from 'app/shared/components/page-header/PageHeader';
import { PaginationComponent } from 'app/shared/components/pagination/Pagination';
import { TableComponent } from 'app/shared/components/table-component/TableComponent';
import { IHeaderItem } from 'app/shared/interfaces/headerItemsInterface';
import { IListData } from 'app/shared/interfaces/listDataInterface';
import { ISearchTypes } from 'app/shared/interfaces/searchTypesInterface';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { Loading } from 'app/shared/util/loading';
import { ASC, DEFAULT_ITEMS_PER_PAGE, DESC } from 'app/shared/util/pagination.constants';
import { getEntities, reset } from './fifth-wheel.reducer';

export const FifthWheel = (props: RouteComponentProps<{ url: string }>) => {
  const dispatch = useAppDispatch();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(props.location, DEFAULT_ITEMS_PER_PAGE, 'id'), props.location.search)
  );

  const fifthWheelList = useAppSelector(state => state.fifthWheel.entities);
  const loading = useAppSelector(state => state.fifthWheel.loading);
  const totalItems = useAppSelector(state => state.fifthWheel.totalItems);
  const [codeSearch, setCodeSearch] = useState<string>('');
  const [truckImplementSearch, setTruckImplementSearch] = useState<string>('');
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [companySearch, setCompanySearch] = useState<string>('');

  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));

  const headers: IHeaderItem[] = [
    { key: 'identificationCode', sortable: true, component: <Translate contentKey="fleetSenseApp.fifthWheel.identificationCode" /> },
    { key: 'truckImplement.licensePlate', sortable: true, component: <Translate contentKey="fleetSenseApp.fifthWheel.truckImplement" /> },
    { key: 'company.name', sortable: true, component: <Translate contentKey="fleetSenseApp.fifthWheel.company" /> },
  ];
  const filteredHeaders = !isAdmin ? headers.filter(header => header.key !== 'company.name') : headers;

  const data: IListData[] = [
    { type: 'text', value: 'identificationCode' },
    { type: 'object', value: 'truckImplement', label: 'licensePlate' },
    { type: 'object', value: 'company', label: 'name', onlyAdmin: true },
    { type: 'editButton', value: 'id' },
    { type: 'deleteButton', value: 'id' },
  ];

  const getAllEntities = useCallback(() => {
    dispatch(reset());
    dispatch(
      getEntities({
        companyId,
        codeSearch,
        truckImplementSearch,
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      })
    );
  }, [paginationState]);

  const sortEntities = () => {
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (props.location.search !== endURL) {
      props.history.replace(`${props.location.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = (event, page) =>
    setPaginationState({
      ...paginationState,
      activePage: page,
    });

  const setActivePage = () => {
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
  };

  useEffect(() => {
    getAllEntities();
  }, [getAllEntities]);

  const searchTypes: ISearchTypes[] = [
    {
      value: codeSearch,
      setValue: setCodeSearch,
      placeholder: translate('fleetSenseApp.fifthWheel.codeSearch'),
      label: translate('fleetSenseApp.fifthWheel.identificationCode'),
    },
    {
      value: truckImplementSearch,
      setValue: setTruckImplementSearch,
      placeholder: translate('fleetSenseApp.fifthWheel.truckImplementSearch'),
      label: translate('fleetSenseApp.fifthWheel.truckImplement'),
    },
  ];

  const handleSearchCompany = (company: { id: number; name: string }) => {
    if (isAdmin) {
      setCompanyId(company.id);
      setCompanySearch(company.name);
      setActivePage();
    }
  };

  const sortClear = () => {
    setCompanyId(null);
    setCompanySearch('');
    setCodeSearch('');
    setTruckImplementSearch('');
    setActivePage();
  };

  const { match } = props;

  return (
    <Box sx={{ backgroundColor: 'primary.main', borderRadius: '8px', paddingTop: '16px', padding: '24px' }}>
      <PageHeader
        headingId="fifth-wheel-heading"
        headingDataCy="FifthWheelHeading"
        contentKey="fleetSenseApp.fifthWheel.home.title"
        buttonTextKey="fleetSenseApp.fifthWheel.home.createLabel"
        match={match}
        showButton={true}
      />

      <PageFilters
        setActivePage={setActivePage}
        searchTypes={searchTypes}
        isAdmin={isAdmin}
        handleSearchCompany={handleSearchCompany}
        searchCompanyName={companySearch}
        sortClear={sortClear}
        showFilters={['company', 'filters', 'clearFilters']}
      />

      {!loading && (
        <TableComponent listData={data} list={fifthWheelList} headers={filteredHeaders} match={match} sortFunction={sort} pagination={paginationState} />
      )}
      {!loading && !fifthWheelList.length && <NoData title="quinta roda" />}
      {loading && <Loading />}
      <PaginationComponent paginationState={paginationState} totalItems={totalItems} onChange={handlePagination} list={fifthWheelList} />
    </Box>
  );
};

export default FifthWheel;
